import {ClassTreeNode} from "../../../models/frontend_entities/ClassTreeNode";
import {StateContext} from "@ngxs/store";
import {Observable} from "rxjs/internal/Observable";
import {finalize, from, throwError} from "rxjs";
import { map, tap } from 'rxjs/operators';
import {CompoClass} from "../../../models/component_class/CompoClass";
import {MapUtils} from "../../../utils/MapUtils";
import {ClassTreeUtils} from "../../../utils/ClassTreeUtils";
import {
  HorizontalClassTreeCheckboxSelectionClientService
} from '../../../content/components/horizontal-class-trees/horizontal-class-tree/horizontal-class-tree-checkbox-selection/horizontal-class-tree-checkbox-selection-client.service';
import { ClassUtils } from '../../../utils/ClassUtils';

export interface BaseReleasedClassesStateModel {
  initialSelection: Set<string>;
  currentSelection: Set<string>;
  currentSelectionOnlyLeafs: Set<string>;
  classTree: ClassTreeNode[];
  allClassIds: string[];
  allLeafClassIds: string[];
  inProgress: boolean;
}

export abstract class BaseReleasedClassesXsState {

  public static getDefaults(initSelection?: Set<string>, currentSelection?: Set<string>) :BaseReleasedClassesStateModel {
    return {
      initialSelection: initSelection ? initSelection :new Set<string>(),
      currentSelection: currentSelection? currentSelection : new Set<string>(),
      currentSelectionOnlyLeafs: new Set<string>,
      classTree: [],
      allClassIds: [],
      allLeafClassIds: [],
      inProgress: false
    };
  }

  protected abstract getCompoClassTree(): Promise<CompoClass[]>;
  protected abstract performSave(state: BaseReleasedClassesStateModel): Observable<any>;
  protected abstract postSave(state: BaseReleasedClassesStateModel, resp?: any): void;

  static classTree(state: BaseReleasedClassesStateModel): ClassTreeNode[] | null {
    return state.classTree;
  }

  static requestInProgress(state: BaseReleasedClassesStateModel): boolean{
    return state.inProgress;
  }

  static selection(state: BaseReleasedClassesStateModel): Set<string> | null {
    return state.currentSelection;
  }

  static selectionOnlyLeafs(state: BaseReleasedClassesStateModel): Set<string> | null {
    return state.currentSelectionOnlyLeafs;
  }

  static allClassIds(state: BaseReleasedClassesStateModel): string[] {
    return state.allClassIds;
  }

  static classReleaseInProgress(state: BaseReleasedClassesStateModel): boolean {
    return state.inProgress;
  }

  static saveRequired(state: BaseReleasedClassesStateModel): boolean {
    return !BaseReleasedClassesXsState.areSetsEqual(state.currentSelection, state.initialSelection);
  }

  static areSetsEqual(set1: Set<string>, set2: Set<string>) {
    return set1.size === set2.size && [...set1].every((x) => set2.has(x));
  }

  protected initSelection(ctx: StateContext<BaseReleasedClassesStateModel>): Observable<void>  {
    return from(this.getCompoClassTree())
      .pipe(
        map((compoClasses, _) => {
          ctx.patchState({
            classTree: this.buildNodesExcludingRoot(compoClasses),
            allClassIds: this.getAllClasIds(compoClasses),
            allLeafClassIds: this.buildLeafNodes(compoClasses),
          });
        }),
        finalize(() => {
          let selectedLeafs = new Set<string>();

          if (ctx.getState().currentSelection.size > 0) {
            selectedLeafs = new Set<string>([...ctx.getState().currentSelection.values()].filter(i => ctx.getState().allLeafClassIds.includes(i)));
          }

          ctx.patchState({
            inProgress: false,
            currentSelectionOnlyLeafs: selectedLeafs
          });
        })
      );
  }

  private buildNodesExcludingRoot(resp: CompoClass[]): ClassTreeNode[] {
    const classMap = MapUtils.arrayToMap(resp, 'id');
    const classTreeNodes = ClassTreeUtils.buildTreeNodes(classMap, false, false, []);
    return [...classTreeNodes[0]?.children];
  }

  private buildLeafNodes(resp: CompoClass[]): string[] {
    return resp
      .filter(c => c.canContainComponent)
      .map(c => c.id);
  }

  private getAllClasIds(resp: CompoClass[]): string[] {
    return resp
      .filter(cl => cl.id !== ClassUtils.INSTANCE_ROOT_CLASS_ID)
      .map(cl => cl.id);
  }

  protected changeSelection(ctx: StateContext<BaseReleasedClassesStateModel>, ids: Set<string>): void  {
    const leafIds = new Set<string>([...ids.values()].filter(i => ctx.getState().allLeafClassIds.includes(i)));
    ctx.patchState({
      currentSelection: ids,
      currentSelectionOnlyLeafs: leafIds
    });
  }

  protected saveSelection(ctx: StateContext<BaseReleasedClassesStateModel>): Observable<void>  {
    const state = ctx.getState();

    if (!state.currentSelection) {
      return throwError(() => new Error('No selection provided'));
    }

    if (!BaseReleasedClassesXsState.saveRequired) {
      return throwError(() => new Error('No save is needed'));
    }

    ctx.patchState({
      inProgress: true
    });

    return from( this.performSave(state))
      .pipe(
        map((resp) => {
          this.postSave(state, resp);
        }),
        finalize(() => ctx.patchState({inProgress: false}))
      );
  }

  protected resetSelection(ctx: StateContext<BaseReleasedClassesStateModel>): void {
    const state = ctx.getState();

    const initialSelection = new Set<string>(state.initialSelection);
    const leafIds = new Set<string>([...state.initialSelection.values()].filter(i => ctx.getState().allLeafClassIds.includes(i)));

    ctx.patchState({
      currentSelection: initialSelection,
      currentSelectionOnlyLeafs: leafIds,
    });
  }
}
